import { useState, useEffect, useCallback } from 'react';
import PaymentFormModal, { PaymentStatus, type PaymentCreatePayload, type PaymentFormData } from '../orderModals/PaymentForModal';

// Types
interface Payment {
    id: string;
    paymentIdx?: number;
    paymentDate: string;
    status: string;
    type: string;
    account: string;
    amount: number;
    method?: string;
    orderId?: string;
    orderName?: string;
    memo?: string;
}

interface Props {
  customerId: string;
  totalContractAmount?: number;
  totalSupplyAmount?: number;
  onSummaryUpdate: (summary: { totalAmount: number; totalPaid: number; remainingAmount: number }) => void;
}

export default function PaymentsTab({ customerId, totalContractAmount, totalSupplyAmount, onSummaryUpdate }: Props) {
  const [payments, setPayments] = useState<Payment[]>([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPayment, setEditingPayment] = useState<PaymentFormData | null>(null);
  
  // 내부 표시용 요약 (부모와 별개로 렌더링 시 사용)
  const [localSummary, setLocalSummary] = useState({ totalAmount: 0, totalPaid: 0, remainingAmount: 0 });

  const parseJsonSafe = async (response: Response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  const mapApiTypeToUi = (type: string) => {
    if (type === 'CONTRACT') return '계약금';
    if (type === 'INTERMEDIATE') return '중도금';
    if (type === 'BALANCE') return '잔금';
    if (type === 'ETC') return '기타';
    return type;
  };

  const mapMethodToUi = (method: string) => {
    if (method === 'TRANSFER') return '계좌이체';
    if (method === 'CARD') return '카드';
    if (method === 'CASH') return '현금';
    return method || '-';
  };

  const pickFirstString = (...values: unknown[]) => {
    for (const value of values) {
      if (typeof value === 'string' && value.trim()) return value.trim();
    }
    return '';
  };

  const toDateInput = (value: string) => {
    if (!value) return '';
    const d = new Date(value);
    if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    if (value.length >= 10) return value.slice(0, 10);
    return '';
  };

  const buildSummaryFromPayments = (rows: Payment[]) => {
    const totalAmount = rows.reduce((acc, cur) => acc + (Number(cur.amount) || 0), 0);
    const totalPaid = rows
      .filter((row) => row.status === PaymentStatus.COMPLETED)
      .reduce((acc, cur) => acc + (Number(cur.amount) || 0), 0);
    const remainingAmount = Math.max(totalAmount - totalPaid, 0);
    return { totalAmount, totalPaid, remainingAmount };
  };

  const fetchPayments = useCallback(async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/payment?customerIdx=${customerId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await parseJsonSafe(response);
      const summary = result?.summary || result?.data?.summary;
      const items = (Array.isArray(result?.list?.items) && result.list.items)
        || (Array.isArray(result?.data?.list?.items) && result.data.list.items)
        || (Array.isArray(result?.items) && result.items)
        || [];

      if (!response.ok) {
        setPayments([]);
        const emptySummary = { totalAmount: totalContractAmount ?? 0, totalPaid: 0, remainingAmount: totalContractAmount ?? 0 };
        setLocalSummary(emptySummary);
        onSummaryUpdate(emptySummary);
        return;
      }

      const rows: Payment[] = items.map((item: any) => ({
        id: String(item.paymentIdx ?? `${item.type}-${item.paidDate}`),
        paymentIdx: item.paymentIdx,
        paymentDate: pickFirstString(item.paidDate, item.date, item.paymentDate, item.paidDt),
        status: item.status || PaymentStatus.COMPLETED,
        type: mapApiTypeToUi(item.type || ''),
        account: pickFirstString(item.bankAccount, item.bankInfo, item.bank_info),
        amount: Number(item.amount || 0),
        method: item.method || 'TRANSFER',
        orderId: item.orderIdx ? String(item.orderIdx) : '',
        orderName: '-',
        memo: item.memo || ''
      }));
      setPayments(rows);

      const tPaid = summary ? Number(summary.totalPaidAmount || 0) : buildSummaryFromPayments(rows).totalPaid;
      // 고객 등록 시 입력한 총 계약금액만 사용 (0원이어도 그대로)
      const tAmount = totalContractAmount ?? 0;
      const nextSummary = {
        totalAmount: tAmount,
        totalPaid: tPaid,
        remainingAmount: tAmount - tPaid,
      };
      setLocalSummary(nextSummary);
      onSummaryUpdate(nextSummary);
    } catch (error) {
      console.error('Failed to fetch payments:', error);
    }
  }, [customerId, onSummaryUpdate]);

  useEffect(() => {
    fetchPayments();
  }, [fetchPayments]);

  // --- API Handlers ---
  const handleSavePayment = async (data: PaymentFormData | PaymentCreatePayload) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!editingPayment) {
        const isCreatePayload = 'payments' in data;
        const payload = isCreatePayload
          ? {
              customerIdx: Number(customerId),
              bankAccount: data.bankAccount,
              memo: data.memo,
              payments: data.payments.map((row) => ({
                paidDate: row.paidDate,
                type: row.type,
                amount: Number(row.amount || 0),
                status: row.status || undefined,
                method: row.method || undefined,
              })),
            }
          : {
              customerIdx: Number(customerId),
              bankAccount: (data as PaymentFormData).bankAccount || undefined,
              memo: (data as PaymentFormData).memo || undefined,
              payments: [
                {
                  paidDate: (data as PaymentFormData).paidDate,
                  type: (data as PaymentFormData).type,
                  amount: Number((data as PaymentFormData).amount || 0),
                  status: (data as PaymentFormData).status || PaymentStatus.COMPLETED,
                  method: (data as PaymentFormData).method || 'TRANSFER',
                },
              ],
            };

        const response = await fetch(`https://api.wedmin.biz/customer/payment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        });
        const result = await parseJsonSafe(response);
        if (!response.ok) {
          throw new Error(result?.message || '등록 실패');
        }
        alert('입금 내역이 등록되었습니다.');
        setShowPaymentModal(false);
        fetchPayments();
        return;
      }

      const form = data as PaymentFormData;
      const response = await fetch(`https://api.wedmin.biz/customer/payment/${editingPayment.paymentIdx}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          customerIdx: Number(customerId),
          paidDate: form.paidDate,
          type: form.type,
          amount: Number(form.amount || 0),
          status: form.status || PaymentStatus.COMPLETED,
          bankAccount: form.bankAccount || undefined,
          method: form.method || 'TRANSFER',
          memo: form.memo || undefined,
        })
      });
      const result = await parseJsonSafe(response);
      if (!response.ok) {
        throw new Error(result?.message || '수정 실패');
      }

      alert('입금 내역이 수정되었습니다.');
      setShowPaymentModal(false);
      setEditingPayment(null);
      fetchPayments();
    } catch (error) {
      console.error('Save Payment Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleDeletePayment = async (paymentId: string) => {
    if (!window.confirm('정말 이 입금 내역을 삭제하시겠습니까?')) return;
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`https://api.wedmin.biz/customer/payment/${paymentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        alert('삭제되었습니다.');
        fetchPayments();
      } else {
        const err = await parseJsonSafe(response);
        alert(`삭제 실패: ${err?.message || '오류가 발생했습니다.'}`);
      }
    } catch (error) {
      console.error('Delete Payment Error:', error);
      alert('오류가 발생했습니다.');
    }
  };

  const handleEditClick = (payment: Payment) => {
    const getTypeKey = (k: string) => {
      if (k === '계약금') return 'CONTRACT';
      if (k === '중도금') return 'INTERMEDIATE';
      if (k === '잔금') return 'BALANCE';
      if (k === '기타') return 'ETC';
      return 'CONTRACT';
    };
    const getMethodKey = (k: string) => {
        if (k === '계좌이체') return 'TRANSFER';
        if (k === '카드') return 'CARD';
        if (k === '현금') return 'CASH';
        return 'TRANSFER';
    }
    const getStatusKey = (s: string) => {
       if (s === '입금 완료' || s === '완료') return 'COMPLETED';
       if (s === '입금 예정' || s === '예정') return 'SCHEDULED';
      if (s === '환불됨') return 'REFUNDED';
      if (s === '취소됨') return 'CANCELLED';
      return s; 
    };

    setEditingPayment({
      paymentIdx: payment.paymentIdx || Number(payment.id),
      amount: payment.amount || 0,
      paidDate: toDateInput(payment.paymentDate) || new Date().toISOString().split('T')[0],
      status: getStatusKey(payment.status),
      type: getTypeKey(payment.type || 'CONTRACT'),
      method: getMethodKey(payment.method || 'TRANSFER'),
      bankAccount: payment.account || '',
      memo: payment.memo || ''
    });
    setShowPaymentModal(true);
  };

  return (
    <div className="space-y-6">
      {/* 입금 통계 */}
      <div className="grid grid-cols-5 gap-4">
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500 mb-1">총 계약금액</p><p className="text-xl font-bold text-gray-900">{localSummary.totalAmount.toLocaleString()}원</p></div>
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center"><i className="ri-file-list-3-line text-xl text-blue-600"></i></div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500 mb-1">총 공급액</p><p className="text-xl font-bold text-purple-600">{(totalSupplyAmount ?? 0).toLocaleString()}원</p></div>
            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center"><i className="ri-hand-coin-line text-xl text-purple-600"></i></div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500 mb-1">입금완료</p><p className="text-xl font-bold text-green-600">{localSummary.totalPaid.toLocaleString()}원</p></div>
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center"><i className="ri-check-line text-xl text-green-600"></i></div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500 mb-1">미입금/예정</p><p className="text-xl font-bold text-red-600">{localSummary.remainingAmount.toLocaleString()}원</p></div>
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center"><i className="ri-time-line text-xl text-red-600"></i></div>
          </div>
        </div>
        <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-gray-500 mb-1">입금률</p><p className="text-xl font-bold text-teal-600">{localSummary.totalAmount > 0 ? Math.round((localSummary.totalPaid / localSummary.totalAmount) * 100) : 0}%</p></div>
            <div className="w-10 h-10 bg-teal-100 rounded-lg flex items-center justify-center"><i className="ri-percent-line text-xl text-teal-600"></i></div>
          </div>
        </div>
      </div>

      {/* 입금 내역 리스트 */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">입금 내역</h3>
          <button onClick={() => { setEditingPayment(null); setShowPaymentModal(true); }} className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors cursor-pointer flex items-center">
            <i className="ri-add-line mr-2"></i> 입금 추가
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">입금일</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">상태</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">타입</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">결제수단</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">계좌/정보</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">금액</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">관리</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {payments.map((payment) => (
                <tr key={payment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payment.paymentDate || '-'}</td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {payment.status === 'COMPLETED' ? '입금 완료' : payment.status === 'SCHEDULED' ? '입금 예정' : payment.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{payment.type}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{mapMethodToUi(payment.method || '')}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{payment.account || '-'}</td>
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{payment.amount.toLocaleString()}원</td>
                  <td className="px-6 py-4 text-right text-sm font-medium">
                    <button onClick={() => handleEditClick(payment)} className="text-blue-600 hover:text-blue-900 mr-3 cursor-pointer">수정</button>
                    <button onClick={() => handleDeletePayment(payment.id)} className="text-red-600 hover:text-red-900 cursor-pointer">삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <PaymentFormModal 
        isOpen={showPaymentModal}
        onClose={() => { setShowPaymentModal(false); setEditingPayment(null); }}
        onSave={handleSavePayment}
        initialData={editingPayment}
      />
    </div>
  );
}

// import type { Payment } from "../../../makeup-payments/page";


// interface Props {
//   totalAmount: number;
//   totalPaid: number;
//   remainingAmount: number;
//   payments: Payment[];
//   onAddPaymentClick: () => void;
//   // 수정/삭제 핸들러 추가
//   onEditClick: (payment: Payment) => void;
//   onDeleteClick: (paymentId: string) => void;
// }

// export default function PaymentsTab({
//   totalAmount,
//   totalPaid,
//   remainingAmount,
//   payments,
//   onAddPaymentClick,
//   onEditClick,
//   onDeleteClick
// }: Props) {
//   return (
//     <div className="space-y-6">
//       {/* 입금 통계 (상단 카드) */}
//       <div className="grid grid-cols-4 gap-6">
//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500 mb-1">총금액</p>
//               <p className="text-2xl font-bold text-gray-900">{totalAmount.toLocaleString()}원</p>
//             </div>
//             <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
//               <i className="ri-file-list-3-line text-2xl text-blue-600"></i>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500 mb-1">입금완료</p>
//               <p className="text-2xl font-bold text-green-600">{totalPaid.toLocaleString()}원</p>
//             </div>
//             <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
//               <i className="ri-check-line text-2xl text-green-600"></i>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500 mb-1">미입금/예정</p>
//               <p className="text-2xl font-bold text-red-600">{remainingAmount.toLocaleString()}원</p>
//             </div>
//             <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
//               <i className="ri-time-line text-2xl text-red-600"></i>
//             </div>
//           </div>
//         </div>

//         <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
//           <div className="flex items-center justify-between">
//             <div>
//               <p className="text-sm text-gray-500 mb-1">입금률</p>
//               <p className="text-2xl font-bold text-teal-600">{totalAmount > 0 ? Math.round((totalPaid / totalAmount) * 100) : 0}%</p>
//             </div>
//             <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center">
//               <i className="ri-percent-line text-2xl text-teal-600"></i>
//             </div>
//           </div>
//         </div>
//       </div>

//       {/* 입금 내역 리스트 */}
//       <div className="bg-white rounded-lg shadow-sm border border-gray-200">
//         <div className="p-6 border-b border-gray-200 flex justify-between items-center">
//           <h3 className="text-lg font-semibold text-gray-900">입금 내역</h3>
//           <button 
//             onClick={onAddPaymentClick}
//             className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors whitespace-nowrap cursor-pointer">
//             <i className="ri-add-line mr-2"></i>
//             입금 추가
//           </button>
//         </div>
//         <div className="overflow-x-auto">
//           <table className="w-full">
//             <thead className="bg-gray-50 border-b border-gray-200">
//               <tr>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   입금일
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   상태
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   입금 타입
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   결제수단
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   입금 계좌 / 정보
//                 </th>
//                 <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   금액
//                 </th>
//                 <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
//                   관리
//                 </th>
//               </tr>
//             </thead>
//             <tbody className="bg-white divide-y divide-gray-200">
//               {payments.map((payment) => (
//                 <tr key={payment.id} className="hover:bg-gray-50">
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <div className="text-sm text-gray-900">{payment.paymentDate}</div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <span
//                       className={`px-2 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
//                         payment.status === 'COMPLETED'
//                           ? 'bg-green-100 text-green-800'
//                           : 'bg-yellow-100 text-yellow-800'
//                       }`}
//                     >
//                       {
//                         payment.status === 'COMPLETED' ? '입금 완료' : 
//                         payment.status === 'SCHEDULED' ? '입금 예정' : 
//                         payment.status === 'REFUNDED' ? '환불됨' : 
//                         payment.status === 'CANCELLED' ? '취소됨' : ''
//                       }
//                     </span>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <div className="text-sm text-gray-900">{payment.type}</div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <div className="text-sm text-gray-500">{payment.method || '-'}</div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <div className="text-sm text-gray-500">{payment.account}</div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap">
//                     <div className="text-sm font-medium text-gray-900">
//                       {payment.amount.toLocaleString()}원
//                     </div>
//                   </td>
//                   <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
//                     <button 
//                       onClick={() => onEditClick(payment)}
//                       className="text-blue-600 hover:text-blue-900 mr-3 cursor-pointer whitespace-nowrap"
//                     >
//                       수정
//                     </button>
//                     <button 
//                       onClick={() => onDeleteClick(payment.id)}
//                       className="text-red-600 hover:text-red-900 cursor-pointer whitespace-nowrap"
//                     >
//                       삭제
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </div>
//     </div>
//   );
// }
